import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import type { RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type ICardRepository } from '@webapp/modules/spending-cards/business-logic/interfaces/ICardRepository'
import { mapAdminCardApiModel } from '@webapp/modules/spending-cards/data-access/mappers/mapAdminCardApiModel'
import { mapCardCountsApiModel } from '@webapp/modules/spending-cards/data-access/mappers/mapCardCountsApiModel'
import { type CreateCardInputData } from '@webapp/modules/spending-cards/data-access/types/CreateCardInputData'
import { type CreateCardPayload } from '@webapp/modules/spending-cards/data-access/types/CreateCardPayload'
import { type GetAdminCardsResponse } from '@webapp/modules/spending-cards/data-access/types/GetAdminCardsResponse'
import type { GetCardCountResponseData } from '@webapp/modules/spending-cards/data-access/types/GetCardCountResponseData'
import { type GetCardCountsResponse } from '@webapp/modules/spending-cards/data-access/types/GetCardCountsResponse'
import { type AdminCardConstructor } from '@webapp/modules/spending-cards/entities/AdminCard'
import { type CardConstructor } from '@webapp/modules/spending-cards/entities/Card'
import { type DetailedCardConstructor } from '@webapp/modules/spending-cards/entities/DetailedCard'
import { type CardStatusEnum } from '@webapp/modules/spending-cards/entities/enums/CardStatusEnum'
import { type IssuingProviderNameEnum } from '@webapp/modules/spending-cards/entities/enums/IssuingProviderNameEnum'
import { mapCardApiModel } from '../../mappers/mapCardApiModel'
import { mapDetailedCardApiModel } from '../../mappers/mapDetailedCardApiModel'
import { type GetAdminCardResponse } from '../../types/GetAdminCardResponse'
import { type GetCardsResponse } from '../../types/GetCardsResponse'
import { type GetDetailedCardResponse } from '../../types/GetDetailedCardResponse'
import { type GetMyCardResponse } from '../../types/GetMyCardResponse'
import { type GetPaymentDetailsIframeUrlResponse } from '../../types/GetPaymentDetailsIframeUrlResponse'

const PAY_API_URL = '/api/v3/pay/'
const PAY_ADMIN_URL = `${PAY_API_URL}admin/`

interface Dependency {
  httpClient: IHttpClient
}

export class CardRepository implements ICardRepository {
  constructor(private readonly deps: Dependency) {}

  // Admin flow repository functions

  async getCardCountsAdmin(
    provider?: IssuingProviderNameEnum,
    params?: RepoFilterParams
  ): Promise<RepoStandardResponse<GetCardCountResponseData> | undefined> {
    try {
      const res = await this.deps.httpClient.get<GetCardCountsResponse>(
        `${PAY_ADMIN_URL}cards/counts/`,
        {
          params: {
            provider,
            ...formatRepoFiltersToApiFilters(params),
          },
        }
      )

      return { data: mapCardCountsApiModel(res.data.data) }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getAdminCards({
    status,
    requireActivation,
    provider,
    params,
  }: {
    status?: CardStatusEnum
    requireActivation?: boolean
    provider?: IssuingProviderNameEnum
    params?: RepoFilterParams
  }): Promise<RepoPaginatedResponse<AdminCardConstructor> | undefined> {
    try {
      const res = await this.deps.httpClient.get<GetAdminCardsResponse>(
        `${PAY_ADMIN_URL}cards/`,
        {
          params: {
            status,
            require_activation: requireActivation,
            provider,
            ...formatRepoFiltersToApiFilters(params),
          },
        }
      )

      const { data, metadata } = res.data

      return {
        data: data.map((card) => mapAdminCardApiModel(card)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  async getAdminCard({
    externalId,
  }: {
    externalId: string
  }): Promise<RepoStandardResponse<AdminCardConstructor> | undefined> {
    try {
      const res = await this.deps.httpClient.get<GetAdminCardResponse>(
        `${PAY_ADMIN_URL}cards/${externalId}/`
      )

      return { data: mapAdminCardApiModel(res.data.data) }
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  async createCard(payload: CreateCardInputData): Promise<void> {
    try {
      await this.deps.httpClient.post<CreateCardPayload>(
        `${PAY_ADMIN_URL}cards/`,
        {
          cardholder_id: payload.cardholderId,
          shipping_address: {
            name: payload.shippingAddress.name,
            address_line_one: payload.shippingAddress.addressLineOne,
            address_line_two: payload.shippingAddress.addressLineTwo,
            postal_code: payload.shippingAddress.postalCode,
            city: payload.shippingAddress.city,
            state_province: payload.shippingAddress.stateProvince,
            country: payload.shippingAddress.country,
          },
        }
      )
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async cancelCardAdmin(externalId: string): Promise<void> {
    try {
      await this.deps.httpClient.post<void>(
        `${PAY_ADMIN_URL}cards/${externalId}/cancel/`
      )
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  async lockCardAdmin(externalId: string): Promise<void> {
    try {
      await this.deps.httpClient.post<void>(
        `${PAY_ADMIN_URL}cards/${externalId}/lock/`
      )
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  async unlockCardAdmin(externalId: string): Promise<void> {
    try {
      await this.deps.httpClient.post<void>(
        `${PAY_ADMIN_URL}cards/${externalId}/unlock/`
      )
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  // Cardholder flow repository functions

  async getCardCounts(
    provider?: IssuingProviderNameEnum,
    params?: RepoFilterParams
  ): Promise<RepoStandardResponse<GetCardCountResponseData> | undefined> {
    try {
      const res = await this.deps.httpClient.get<GetCardCountsResponse>(
        `${PAY_API_URL}cards/counts/`,
        {
          params: {
            provider,
            ...formatRepoFiltersToApiFilters(params),
          },
        }
      )

      return { data: mapCardCountsApiModel(res.data.data) }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getMyPhysicalCard(): Promise<
    RepoStandardResponse<CardConstructor | null> | undefined
  > {
    try {
      const response = await this.deps.httpClient.get<GetMyCardResponse>(
        `${PAY_API_URL}my-card/`
      )

      const { card } = response.data.data

      return { data: !!card ? mapCardApiModel(card) : null }
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  async getMyCards({
    status,
    requireActivation,
    provider,
    params,
  }: {
    status: CardStatusEnum
    requireActivation: boolean
    provider?: IssuingProviderNameEnum
    params?: RepoFilterParams
  }): Promise<RepoPaginatedResponse<CardConstructor> | undefined> {
    try {
      const res = await this.deps.httpClient.get<GetCardsResponse>(
        `${PAY_API_URL}cards/`,
        {
          params: {
            status,
            require_activation: requireActivation,
            provider,
            ...formatRepoFiltersToApiFilters(params),
          },
        }
      )

      const { data, metadata } = res.data

      return {
        data: data.map((card) => mapCardApiModel(card)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  async getCardDetails({
    externalId,
  }: {
    externalId: string
  }): Promise<RepoStandardResponse<DetailedCardConstructor> | undefined> {
    try {
      const res = await this.deps.httpClient.get<GetDetailedCardResponse>(
        `${PAY_API_URL}cards/${externalId}/`
      )

      return { data: mapDetailedCardApiModel(res.data.data) }
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  getCardPanToken(): Promise<void> {
    return Promise.resolve(undefined)
  }

  async activateCard(externalId: string): Promise<void> {
    try {
      await this.deps.httpClient.post<void>(
        `${PAY_API_URL}cards/${externalId}/activate/`
      )
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  async cancelCard(externalId: string): Promise<void> {
    try {
      await this.deps.httpClient.post<void>(
        `${PAY_API_URL}cards/${externalId}/cancel/`
      )
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  async lockCard(externalId: string): Promise<void> {
    try {
      await this.deps.httpClient.post<void>(
        `${PAY_API_URL}cards/${externalId}/lock/`
      )
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  async unlockCard(externalId: string): Promise<void> {
    try {
      await this.deps.httpClient.post<void>(
        `${PAY_API_URL}cards/${externalId}/unlock/`
      )
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }

  async getPaymentDetailsIframeUrl(
    externalId: string,
    customCss: Record<string, Record<string, string>> = {},
    scaToken?: string
  ): Promise<RepoStandardResponse<{ url: string }>> {
    try {
      const res = await this.deps.httpClient.post<
        GetPaymentDetailsIframeUrlResponse,
        { custom_css: Record<string, Record<string, string>> }
      >(
        `/api/v3/pay/cards/${externalId}/sensitive-card-details-iframe-url/`,
        {
          custom_css: customCss,
        },
        { headers: scaToken ? { 'x-sca-token': scaToken } : undefined }
      )

      const data = res.data.data

      return {
        data,
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getPinIframeUrl(
    externalId: string,
    customCss: Record<string, Record<string, string>> = {},
    scaToken?: string
  ): Promise<RepoStandardResponse<{ url: string }>> {
    try {
      const res = await this.deps.httpClient.post<
        GetPaymentDetailsIframeUrlResponse,
        { custom_css: Record<string, Record<string, string>> }
      >(
        `/api/v3/pay/cards/${externalId}/card-pin-iframe-url/`,
        {
          custom_css: customCss,
        },
        { headers: scaToken ? { 'x-sca-token': scaToken } : undefined }
      )

      const data = res.data.data

      return {
        data,
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getRequestableCards({
    params,
  }: {
    params?: RepoFilterParams
  }): Promise<RepoPaginatedResponse<CardConstructor> | undefined> {
    try {
      const res = await this.deps.httpClient.get<GetCardsResponse>(
        `${PAY_API_URL}cards/requestable/`,
        {
          params: {
            ...formatRepoFiltersToApiFilters({
              orderBy: 'cardholder_first_name',
              ...params,
            }),
          },
        }
      )

      const { data, metadata } = res.data

      return {
        data: data.map((card) => mapCardApiModel(card)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }
}
